import { simulatePools } from "./advance-pool.helper"

/**
 * Lógica PURA del tooltip de hover del avance lineal (picking + breakdown). Sin
 * DOM reactivo ni Vue: el componente mantiene los listeners y el estado del
 * tooltip; aquí vive el hit-test y el cálculo del desglose. El picking es una de
 * las "costuras" de la visión-norte (reutilizable por el visor unificado).
 */

/**
 * Hit-test del puntero contra `targets` (grupos/meshes THREE). Calcula las NDC
 * en `pointer` (THREE.Vector2, mutado), lanza el rayo y sube desde el mesh hit
 * hasta encontrar `userData.handle` (en el group raíz del carril o en el mesh
 * hitbox del overlay). Devuelve { handle, entity } o null.
 *
 * @param {THREE.Raycaster} raycaster
 * @param {THREE.Vector2}   pointer     - se escribe con las NDC
 * @param {THREE.Camera}    camera
 * @param {{clientX:number, clientY:number}} event
 * @param {HTMLCanvasElement} canvas
 * @param {Array} targets               - grupos/meshes a intersectar
 * @returns {?{handle: string, entity: ?object}}
 */
export function pickHandleAtPointer(raycaster, pointer, camera, event, canvas, targets) {
  if (!raycaster || !camera || !canvas || !targets || targets.length === 0) return null

  const rect = canvas.getBoundingClientRect()
  pointer.x = ((event.clientX - rect.left) / rect.width)  * 2 - 1
  pointer.y = -((event.clientY - rect.top)  / rect.height) * 2 + 1
  raycaster.setFromCamera(pointer, camera)

  const hits = raycaster.intersectObjects(targets, true)
  if (hits.length === 0) return null

  // El handle vive en el group raíz (carril) o en el mesh hitbox (overlay).
  let obj = hits[0].object
  while (obj && obj.userData?.handle == null) obj = obj.parent
  const handle = obj?.userData?.handle
  if (!handle) return null

  // Fallback de entity desde el userData del hit (para hitboxes del overlay).
  const entity = hits[0].object?.userData?.entity || obj.userData?.entity || null
  return { handle, entity }
}

/**
 * Desglose del tooltip para un handle:
 *   - Capas CON rechazos → distribución vigente vía modelo de bolsas
 *     (simulatePools): por-iniciar + etapas vigentes + rechazados por estado.
 *   - Capas SIN rechazos (zanjas) → suma cruda por estado (procesos paralelos).
 * Coherente con la franja pintada (currentStateSegments) y con el modal.
 *
 * @param {?object} data          - state del handle ({ segments, totalLength })
 * @param {Array}   layerStatuses - statuses de la capa ({ value, label, color, weight })
 * @param {number}  totalLength
 * @param {{pending?:string, rejected?:string}} labels - textos i18n ya traducidos
 * @returns {Array<{value, label, color, meters}>}
 */
export function computeHoverBreakdown(data, layerStatuses, totalLength, labels = {}) {
  const rawSegments = (data && data.segments) || []
  const statuses = layerStatuses || []
  const hasRejects = statuses.some(s => Number(s.weight) < 0)
  const eps = 1e-6

  if (hasRejects) {
    const breakdown = []
    const pools = simulatePools(rawSegments, statuses, totalLength)
    if ((pools.unstarted || 0) > eps) {
      breakdown.push({ value: '__unstarted', label: labels.pending || '', color: '#9ca3af', meters: pools.unstarted })
    }
    for (let k = 0; k < pools.M; k++) {
      if ((pools.bags[k] || 0) > eps) {
        const st = statuses[pools.positives[k]]
        breakdown.push({ value: st.value, label: st.label, color: st.color, meters: pools.bags[k] })
      }
    }
    if (pools.rejectedByStatus) {
      for (const [sv, meters] of pools.rejectedByStatus) {
        if ((meters || 0) <= eps) continue
        const rej = statuses.find(s => s.value === sv)
        breakdown.push({ value: 'rej-' + sv, label: rej ? rej.label : (labels.rejected || ''), color: (rej && rej.color) || '#e17055', meters })
      }
    }
    return breakdown
  }

  // Zanjas (sin rechazos): suma cruda por estado (procesos paralelos).
  const map = new Map()
  for (const seg of rawSegments) {
    const key = seg.status
    if (!map.has(key)) {
      map.set(key, { value: seg.status, label: seg.statusLabel || '', color: seg.statusColor || '#9ca3af', meters: 0 })
    }
    map.get(key).meters += seg.meters || 0
  }
  return [...map.values()]
}
